import { z } from 'zod';

// Canonical mirror of the BE `concern` pgEnum. Deliberately NOT the
// portal-helpline CONCERN_VALUES list: that one is the student self-select
// vocabulary and is a strict subset, so parsing responses with it rejects any
// concern a psychologist stamped from the wider clinical picker.
import { concernEnum } from '@/features/sessions/constants/concerns';

export const chatSessionStatusEnum = z.enum(['active', 'closed'] as const);
export type ChatSessionStatus = z.infer<typeof chatSessionStatusEnum>;

export const chatEndedByEnum = z.enum(['student', 'psychologist', 'system_timeout'] as const);
export type ChatEndedBy = z.infer<typeof chatEndedByEnum>;

/** Clinical duration classification. Null for client callers (the BE strips
 *  it); never rendered on the student surface. */
export const chatClassificationEnum = z.enum(['enquiry', 'chat_session'] as const);
export type ChatClassification = z.infer<typeof chatClassificationEnum>;

export const chatSenderRoleEnum = z.enum(['student', 'psychologist', 'system'] as const);
export type ChatSenderRole = z.infer<typeof chatSenderRoleEnum>;

/**
 * Enriched chat row returned by GET /chat-sessions and GET /chat-sessions/:id.
 * `classification` is null and `concerns` [] for client callers; the BE
 * strips clinical metadata for students.
 */
export const chatSessionSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  organizationId: z.string(),
  locationId: z.string(),
  status: chatSessionStatusEnum,
  clientName: z.string().nullable(),
  locationName: z.string().nullable(),
  /** True while no psychologist has replied yet (inbox Waiting badge). */
  waiting: z.boolean(),
  /** True while the student is owed a reply — drives the sidebar unread count.
   *  Superset of `waiting`: also true when a student writes back after a reply. */
  awaitingReply: z.boolean(),
  lastMessageAt: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
  lastResponderName: z.string().nullable(),
  createdAt: z.string(),
  endedAt: z.string().nullable(),
  endedBy: chatEndedByEnum.nullable(),
  classification: chatClassificationEnum.nullable(),
  concerns: z.array(concernEnum),
});
export type ChatSession = z.infer<typeof chatSessionSchema>;

/**
 * POST /chat-sessions returns the raw chat row (no enrichment). Only the
 * fields the FE navigates on are parsed; the rest are ignored.
 */
export const createdChatSessionSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  status: chatSessionStatusEnum,
  createdAt: z.string(),
});
export type CreatedChatSession = z.infer<typeof createdChatSessionSchema>;

/**
 * POST /chat-sessions/:id/end returns the raw closed row (no enrichment;
 * classification null / concerns [] for client callers). Parse only what the
 * FE reads back.
 */
export const endedChatSessionSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  status: chatSessionStatusEnum,
  endedAt: z.string().nullable(),
  endedBy: chatEndedByEnum.nullable(),
  createdAt: z.string(),
});
export type EndedChatSession = z.infer<typeof endedChatSessionSchema>;

export const chatMessageSchema = z.object({
  id: z.string(),
  chatSessionId: z.string(),
  /** Null for system messages. */
  senderId: z.string().nullish(),
  senderRole: chatSenderRoleEnum,
  senderName: z.string().nullable(),
  content: z.string(),
  createdAt: z.string(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const chatMessagesPaginationSchema = z.object({
  limit: z.number(),
  offset: z.number(),
  total: z.number(),
});
export type ChatMessagesPagination = z.infer<typeof chatMessagesPaginationSchema>;

export interface ChatMessagesPage {
  messages: ChatMessage[];
  pagination: ChatMessagesPagination;
}

export const typingSignalSchema = z.object({
  expiresAt: z.string(),
});
export type TypingSignal = z.infer<typeof typingSignalSchema>;

/** Max message length enforced by the BE (sendChatMessageSchema). */
export const CHAT_MESSAGE_MAX_CHARS = 4000;

/** System message strings the backend inserts verbatim. Kept here so the
 *  conversation view can style them without string-matching in JSX. */
export const CHAT_SYSTEM_MESSAGES = {
  autoResponse: 'A psychologist will be connecting with you shortly.',
  idleTimeout: 'This chat was closed due to inactivity.',
} as const;
